import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, MapPin, Plus, AlertTriangle, Briefcase, Camera, LayoutList, Columns3, Search, Filter, Trash2, Sparkles, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useTrade } from "@/contexts/TradeContext";
import { getPipelineStages, stageBadgeColor, humanizeStage } from "@/lib/tradeStages";
import { offlineDB } from "@/lib/offlineDB";

interface Job {
  id: string;
  job_number: string;
  status: string;
  property_address: string;
  property_city: string;
  property_state: string;
  property_type: string | null;
  owner_name: string;
  estimated_value: number;
  created_at: string;
  photos: { id: string; ai_damage_detected: boolean }[];
}

const Jobs = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, orgId, loading: authLoading } = useAuth();
  const { t, activeTradePack } = useTrade();
  const jobsLabel = t("jobs", "Jobs");
  const jobLabel = t("job", "Job");
  const tradeName = activeTradePack?.trade_code === "roofing" ? "roofing" : "";
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [view, setView] = useState<"list" | "pipeline">("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeMode, setActiveMode] = useState<"retail" | "insurance" | "commercial" | "all">("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (orgId) {
      loadJobs();
    }
  }, [orgId]);

  useEffect(() => {
    const statusParam = searchParams.get('status');
    const viewParam = searchParams.get('view');
    if (statusParam) setStatusFilter(statusParam);
    if (viewParam && (viewParam === 'retail' || viewParam === 'insurance' || viewParam === 'commercial' || viewParam === 'all')) {
      setActiveMode(viewParam as "retail" | "insurance" | "commercial" | "all");
    }
  }, [searchParams]);

  const loadJobs = async () => {
    if (!orgId) return;
    
    setLoading(true);
    
    // Handle offline mode - load from local IndexedDB instead of backend
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      try {
        const inspections = await offlineDB.inspections.toArray();

        if (!inspections.length) {
          setJobs([]);
          toast.info("Offline: no locally saved jobs yet");
        } else {
          const offlineJobs: Job[] = inspections.map((inspection) => {
            const form = inspection.formData || {};
            return {
              id: inspection.jobId || inspection.localId,
              job_number: form.job_number ?? inspection.localId,
              status: form.status ?? "offline",
              property_address: form.property_address ?? "Offline inspection",
              property_city: form.property_city ?? "",
              property_state: form.property_state ?? "",
              property_type: form.property_type ?? null,
              owner_name: form.owner_name ?? "",
              estimated_value: form.estimated_value ?? 0,
              created_at: inspection.createdAt,
              photos: [],
            };
          });

          setJobs(offlineJobs);
          toast.info("Offline: showing locally saved jobs");
        }
      } catch (error) {
        console.error("Error loading offline jobs:", error);
        toast.error("Offline: could not load local jobs");
        setJobs([]);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          photos:photos(id, ai_damage_detected)
        `)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error("Error loading jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => stageBadgeColor(status);

  const pipelineStages = getPipelineStages(activeTradePack?.job_stages);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchQuery ||
      job.owner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.property_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.job_number?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    
    // When filtering by status (from Dashboard), show all jobs regardless of property_type
    // Only apply property_type filter when on "all" status
    const matchesMode = statusFilter !== 'all' || activeMode === 'all' || 
      job.property_type?.toLowerCase() === activeMode.toLowerCase();

    return matchesSearch && matchesStatus && matchesMode;
  });

  const handleDragStart = (e: React.DragEvent, jobId: string) => {
    e.dataTransfer.setData('jobId', jobId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const jobId = e.dataTransfer.getData('jobId');
    
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status: newStatus as any })
        .eq('id', jobId);

      if (error) throw error;

      setJobs(jobs.map(job => 
        job.id === jobId ? { ...job, status: newStatus } : job
      ));
      
      toast.success('Job status updated');
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error('Failed to update job status');
    }
  };

  const handleDeleteJob = async () => {
    if (!jobToDelete) return;

    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobToDelete.id);

      if (error) throw error;

      setJobs(jobs.filter(job => job.id !== jobToDelete.id));
      toast.success('Job deleted successfully');
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const openDeleteDialog = (e: React.MouseEvent, job: Job) => {
    e.stopPropagation();
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{jobsLabel}</h1>
          <p className="text-muted-foreground mt-1">
            Manage and track all your{tradeName ? ` ${tradeName}` : ""} {jobsLabel.toLowerCase()}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-action hover:bg-action-hover">
              <Plus className="w-4 h-4 mr-2" />
              New {jobLabel}
              <ChevronDown className="w-4 h-4 ml-2 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem onClick={() => navigate("/jobs/new/ai")} className="py-3">
              <Sparkles className="w-4 h-4 mr-2 text-primary" />
              <div className="flex flex-col">
                <span className="font-medium">Start Project with AI</span>
                <span className="text-xs text-muted-foreground">Photos → scope → estimate</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/jobs/new")} className="py-3">
              <Plus className="w-4 h-4 mr-2" />
              <div className="flex flex-col">
                <span className="font-medium">Create Manually</span>
                <span className="text-xs text-muted-foreground">Standard form</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto pb-2 md:pb-0">
          {/* Mode Filter */}
          <Button
            variant={activeMode === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveMode("all")}
            className={cn("whitespace-nowrap", activeMode === "all" ? "bg-action hover:bg-action-hover" : "")}
          >
            All
          </Button>
          <Button
            variant={activeMode === "retail" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveMode("retail")}
            className={cn("whitespace-nowrap", activeMode === "retail" ? "bg-action hover:bg-action-hover" : "")}
          >
            Retail
          </Button>
          <Button
            variant={activeMode === "insurance" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveMode("insurance")}
            className={cn("whitespace-nowrap", activeMode === "insurance" ? "bg-action hover:bg-action-hover" : "")}
          >
            Insurance
          </Button>
          <Button
            variant={activeMode === "commercial" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveMode("commercial")}
            className={cn("whitespace-nowrap", activeMode === "commercial" ? "bg-action hover:bg-action-hover" : "")}
          >
            Commercial
          </Button>
        </div>

        <div className="flex items-center gap-2 w-full md:flex-1 md:max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 border rounded-lg p-1 w-full md:w-auto">
          <Button
            variant={view === "list" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("list")}
            className="gap-2 flex-1 md:flex-none"
          >
            <LayoutList className="w-4 h-4" />
            <span className="hidden sm:inline">List</span>
          </Button>
          <Button
            variant={view === "pipeline" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("pipeline")}
            className="gap-2 flex-1 md:flex-none"
          >
            <Columns3 className="w-4 h-4" />
            <span className="hidden sm:inline">Pipeline</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : jobs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Briefcase className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No jobs yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Get started by creating your first job
            </p>
            <Button onClick={() => navigate("/jobs/new")}>
              <Plus className="w-4 h-4 mr-2" />
              Create Job
            </Button>
          </CardContent>
        </Card>
      ) : view === "list" ? (
        <div className="grid gap-3 md:gap-4">
          {filteredJobs.map((job) => {
              const damageCount = job.photos?.filter(p => p.ai_damage_detected).length || 0;
              
            return (
              <Card
                key={job.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/jobs/${job.id}`)}
              >
                <CardHeader className="p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                    <div className="flex-1 w-full">
                      <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-2">
                        {job.job_number && (
                          <span className="text-xs md:text-sm font-mono text-muted-foreground">
                            {job.job_number}
                          </span>
                        )}
                        <Badge className={cn(getStatusColor(job.status), "text-xs")}>
                          {humanizeStage(job.status)}
                        </Badge>
                        {damageCount > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {damageCount}
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-base md:text-lg">{job.owner_name || 'Unnamed Job'}</CardTitle>
                      <CardDescription className="flex items-start gap-1 mt-1 text-xs md:text-sm">
                        <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">
                          {job.property_address}
                          {job.property_city && `, ${job.property_city}`}
                          {job.property_state && `, ${job.property_state}`}
                        </span>
                      </CardDescription>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-3">
                      <div className="text-left sm:text-right">
                        {job.estimated_value && (
                          <div className="text-base md:text-lg font-semibold">
                            ${job.estimated_value.toLocaleString()}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(job.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-muted-foreground hover:text-destructive flex-shrink-0"
                        onClick={(e) => openDeleteDialog(e, job)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {job.photos && job.photos.length > 0 && (
                  <CardContent className="p-4 md:p-6 pt-0">
                    <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                      <Camera className="w-4 h-4 flex-shrink-0" />
                      <span>
                        {job.photos.length} photo{job.photos.length !== 1 ? 's' : ''}
                        {damageCount > 0 && (
                          <span className="text-destructive font-medium">
                            {' • '}{damageCount} with damage
                          </span>
                        )}
                      </span>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        /* Pipeline View */
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {pipelineStages.map((stage) => {
            const stageJobs = filteredJobs.filter(job => job.status === stage.id);
            
            return (
              <div
                key={stage.id}
                className={cn("space-y-3", stage.color)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm uppercase tracking-wide">{stage.label}</h3>
                  <Badge variant="secondary">{stageJobs.length}</Badge>
                </div>
                
                <div className="space-y-2">
                  {stageJobs.map((job) => {
                    const damageCount = job.photos?.filter(p => p.ai_damage_detected).length || 0;
                    
                    return (
                      <Card
                        key={job.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, job.id)}
                        className="cursor-move hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/jobs/${job.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm line-clamp-1">
                                  {job.owner_name || 'Unnamed Job'}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {job.estimated_value && (
                                  <p className="text-sm font-semibold whitespace-nowrap">
                                    ${(job.estimated_value / 1000).toFixed(0)}K
                                  </p>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                  onClick={(e) => openDeleteDialog(e, job)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {job.property_address}
                            </p>
                            <div className="flex items-center gap-1 flex-wrap">
                              {job.job_number && (
                                <Badge variant="outline" className="text-xs">
                                  {job.job_number}
                                </Badge>
                              )}
                              {damageCount > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="w-2 h-2 mr-1" />
                                  {damageCount}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {stageJobs.length === 0 && (
                    <div className="text-center py-8 text-xs text-muted-foreground">
                      No jobs in this stage
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the job for <strong>{jobToDelete?.owner_name}</strong>?
              This action cannot be undone and will permanently delete all associated data including photos, documents, and tasks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteJob}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Jobs;